/*
 * Typer for TV-plakater, plakatstudioet og visningsplanen.
 * Se API-kontrakten «TV-plakatstudio og visningsplan».
 */

export type TvPosterLayout =
  | "ANNOUNCEMENT"
  | "EVENT"
  | "PROGRAM"
  | "FUNDRAISER"
  | "IMAGE";

export type TvPosterTheme =
  | "NIGHT"
  | "EMERALD"
  | "RAMADAN"
  | "OCEAN"
  | "EVENING"
  | "LIGHT";

export interface TvPosterDate {
  date: string | null;
  from: string | null;
  to: string | null;
  note: string | null;
  /** Faste arrangementer: «Hver søndag», «Annenhver lørdag». `date` er da første gang, eller null. */
  repeat?: string | null;
}

export interface TvPosterSpeaker {
  name: string | null;
  role: string | null;
  photoUrl: string | null;
}

export interface TvPosterProgramRow {
  time: string;
  text: string;
}

export interface TvPosterQr {
  url: string | null;
  imageUrl: string | null;
  label: string | null;
}

export interface TvPosterVipps {
  number: string | null;
  account: string | null;
}

export interface TvPosterGoal {
  project: string | null;
  target: number | null;
  raised: number | null;
}

export interface TvPosterContact {
  name: string | null;
  phone: string | null;
}

/** Plakatdesign. `null` på en blokk betyr at blokken er slått av. */
export interface TvPosterDesign {
  layout: TvPosterLayout;
  theme: TvPosterTheme;
  eyebrow: string | null;
  title: string;
  subtitle: string | null;
  body: string | null;
  arabic: string | null;
  date: TvPosterDate | null;
  place: string | null;
  speaker: TvPosterSpeaker | null;
  program: TvPosterProgramRow[] | null;
  qr: TvPosterQr | null;
  imageUrl: string | null;
  vipps: TvPosterVipps | null;
  goal: TvPosterGoal | null;
  signup: string | null;
  contact: TvPosterContact | null;
  ownImageUrl: string | null;
}

/** Body til `suggest`. Vedleggene lastes opp via `assets` først. */
export interface TvPosterSuggestRequest {
  /** Fritekst, maks 300 tegn. */
  prompt: string;
  /** Annet bilde som skal på plakaten. */
  imageUrl: string | null;
  /** QR-kode som bilde. */
  qrImageUrl: string | null;
  /** Bilde av foredragsholderen. */
  speakerPhotoUrl: string | null;
}

/** Svar fra `suggest`. `logId` er raden i AI-loggen (null hvis ikke logget). */
export interface TvPosterSuggestResponse {
  suggestions: TvPosterDesign[];
  logId: number | null;
}

export interface TvPosterSaveRequest {
  design: TvPosterDesign;
  activatedForTv: boolean;
  activated: boolean;
  priority: number;
  /**
   * Kun på POST: AI-loggen plakaten kom fra (valgt med «Bruk dette», ev.
   * justert), så superadmin ser at forslaget ble lagret.
   */
  aiLogId?: number | null;
}

/** Vedlegg i en AI-forespørsel. */
export type TvAiLogAttachment = "IMAGE" | "QR" | "SPEAKER";

export const AI_LOG_ATTACHMENT_LABELS: Record<TvAiLogAttachment, string> = {
  IMAGE: "Bilde",
  QR: "QR-kode",
  SPEAKER: "Foredragsholder",
};

/** Én AI-forespørsel i superadmin sin «AI-logg». */
export interface TvAiLogItem {
  id: number;
  /** ISO, lokal tid. */
  createdAt: string;
  orgNr: string;
  mosqueName: string | null;
  email: string | null;
  prompt: string;
  attachments: TvAiLogAttachment[] | null;
  suggestions: TvPosterDesign[] | null;
  model: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  durationMs: number | null;
  error: string | null;
  savedPosterId: number | null;
  savedAt: string | null;
  savedPosterImage: string | null;
  savedPosterTitle: string | null;
}

export interface TvAiLogStats {
  generations: number;
  last7Days: number;
  mosques: number;
  users: number;
  saved: number;
  errors: number;
  studioPosters: number;
  tokens: number;
}

/** `GET /api/v1/admin/tv-posters/ai-log`. `page` er 0-basert. */
export interface TvAiLogResponse {
  stats: TvAiLogStats;
  items: TvAiLogItem[];
  total: number;
  page: number;
  size: number;
}

/** Plakat fra `/api/v1/poster/...`. `design != null` = laget i studioet. */
export interface Poster {
  id: number;
  date: string;
  tittel: string;
  tekst: string;
  plakat: string;
  mosqueOrgNr: string;
  activated: boolean;
  activatedForTv: boolean;
  priority: number;
  design?: TvPosterDesign | null;
  /**
   * Stående 4:5-utgave (1080×1350) av studio-plakater. Null for opplastede
   * plakater og studio-plakater lagret før den fantes.
   */
  plakatPortrait?: string | null;
  /** TV-ene plakaten vises på; tom = alle. Mangler på eldre servere. */
  tvDeviceIds?: number[];
}

/** `GET/PUT /api/v1/mosques/{orgNr}/tv-settings`. */
export interface TvSettings {
  /** Sekunder per plakat i karusellen, 5–120 (standard 15). */
  slideSeconds: number;
  /** Minutter adhan-skjermen vises, 1–30 (standard 5). Mangler på eldre servere. */
  adhanMinutes?: number;
  /** Minutter fellesbønn-/iqamah-skjermen vises (også soloppgang), 1–30 (standard 10). */
  iqamahMinutes?: number;
  /** Minutter jummah-skjermen vises, 1–30 (standard 15). */
  jummahMinutes?: number;
  /** Dagens hadith først i plakatrotasjonen. Mangler på eldre servere. */
  showDailyHadith?: boolean;
  /** Dagens vers først i plakatrotasjonen. */
  showDailyQuran?: boolean;
  /** Fullskjerm med dagens vers og hadith hvert N. minutt (5–120); 0 = av. */
  dailyBreakMinutes?: number;
  /** Sekunder hver av de to plakatene vises i fullskjerm (15–300, standard 60). */
  dailyBreakSeconds?: number;
}

/** Valgene for dagens vers og hadith på TV. */
export interface TvDailySettings {
  showDailyHadith: boolean;
  showDailyQuran: boolean;
  dailyBreakMinutes: number;
  dailyBreakSeconds: number;
}

/** `GET /api/v1/tv/daily-posters/today`: plakaten for dagens vers eller hadith. */
export interface DailyPoster {
  id: number;
  kind: "QURAN" | "HADITH";
  dateIso: string;
  title: string;
  plakat: string;
  plakatPortrait: string;
  updatedAt: string;
}

/** «Varselskjermer på TV»: hvor lenge hver varselskjerm står, i minutter. */
export interface TvAlertMinutes {
  adhanMinutes: number;
  iqamahMinutes: number;
  jummahMinutes: number;
}

export type TvAlertKey = keyof TvAlertMinutes;

/**
 * Hvordan en tidsstyrt visning vises:
 * - `POSTER_WITH_PRAYERS`: plakaten stort + bønnetidene nederst (standard)
 * - `POSTER_ONLY`: plakaten i hele skjermen
 * - `CAROUSEL`: TV-ens vanlige visning, men karusellen viser bare regelens plakater
 */
export type TvScheduleDisplayMode =
  | "POSTER_WITH_PRAYERS"
  | "POSTER_ONLY"
  | "CAROUSEL";

/** «Tidsstyrt visning» i visningsplanen. Rekkefølgen i listen = prioritet. */
export interface TvScheduleRule {
  id: number | null;
  name: string;
  posterIds: number[];
  repeat: "WEEKLY" | "DATE";
  /** ISO-ukedager, 1 = mandag … 7 = søndag. */
  days: number[];
  /** «YYYY-MM-DD». */
  date: string | null;
  /** «HH:mm». */
  from: string;
  to: string;
  /** Mangler på eldre regler; da gjelder `POSTER_WITH_PRAYERS`. */
  displayMode?: TvScheduleDisplayMode;
  /** TV-ene visningen gjelder; tom = alle. Mangler på eldre servere. */
  tvDeviceIds?: number[];
}

export const LAYOUTS: Array<{
  value: TvPosterLayout;
  name: string;
  description: string;
}> = [
  {
    value: "ANNOUNCEMENT",
    name: "Kunngjøring",
    description: "Stor tittel, tekst og bilde",
  },
  {
    value: "EVENT",
    name: "Arrangement",
    description: "Stor dato, tid, sted og QR",
  },
  {
    value: "PROGRAM",
    name: "Program",
    description: "Liste med tider og punkter",
  },
  {
    value: "FUNDRAISER",
    name: "Innsamling",
    description: "Mål, fremdrift, Vipps og QR",
  },
  {
    value: "IMAGE",
    name: "Eget bilde",
    description: "Last opp en ferdig plakat",
  },
];

export const THEMES: Array<{
  value: TvPosterTheme;
  name: string;
  background: string;
  accent: string;
}> = [
  { value: "NIGHT", name: "Natt", background: "#0B2A33", accent: "#8FE0B6" },
  {
    value: "EMERALD",
    name: "Smaragd",
    background: "#0B3A2A",
    accent: "#E3C27B",
  },
  {
    value: "RAMADAN",
    name: "Ramadan",
    background: "#181B44",
    accent: "#F1C56A",
  },
  { value: "OCEAN", name: "Hav", background: "#0B264D", accent: "#86CBF2" },
  {
    value: "EVENING",
    name: "Kveld",
    background: "#3B1725",
    accent: "#F4B4A4",
  },
  { value: "LIGHT", name: "Lys", background: "#F3F6F4", accent: "#1D7A52" },
];

export const layoutName = (layout: TvPosterLayout) =>
  LAYOUTS.find((l) => l.value === layout)?.name ?? layout;

export const themeName = (theme: TvPosterTheme) =>
  THEMES.find((t) => t.value === theme)?.name ?? theme;
